import React, { type PropsWithChildren, createContext, useContext } from 'react';
import type { Modifier } from '@atlaskit/popper';
import { fg } from '@atlassian/jira-feature-gating';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';

export type EditViewPopupModifier = Modifier<'closeOnThreshold', object>;

export interface EditViewPopupModifiersContextValue {
	modifiers: () => EditViewPopupModifier[];
}

export interface EditViewPopupModifiersProviderProps extends PropsWithChildren {
	modifiers: () => EditViewPopupModifier[];
}

const EditViewPopupModifiersContext = createContext<EditViewPopupModifiersContextValue | null>(
	null,
);

const EditViewPopupModifiersProviderBase = ({
	children,
	modifiers,
}: EditViewPopupModifiersProviderProps) => {
	const contextValue: EditViewPopupModifiersContextValue = {
		modifiers,
	};

	return (
		<EditViewPopupModifiersContext.Provider value={contextValue}>
			{children}
		</EditViewPopupModifiersContext.Provider>
	);
};

const EditViewPopupModifiersProviderDisabled = ({ children }: PropsWithChildren) => <>{children}</>;

export const EditViewPopupModifiersProvider = componentWithCondition(
	// eslint-disable-next-line jira/ff/no-preconditioning
	() => fg('issue_table_frozen_columns') && fg('jira_list_frozen_column_bugfix'),
	EditViewPopupModifiersProviderBase,
	EditViewPopupModifiersProviderDisabled,
);

export const useEditViewPopupModifiersContext = (): EditViewPopupModifier[] => {
	const context = useContext(EditViewPopupModifiersContext);

	// Gracefully fallback to empty modifiers if no provider is rendered
	if (context === null) {
		return [];
	}

	return context?.modifiers() || [];
};
